import type {
  ApplicabilityItem,
  ApplicabilityTargetCell,
  UpdateApplicabilityInput,
} from "@compliance-sage/contracts";
import { api } from "../../../api/client";

export type Section = "MAP" | "MATRIX" | "GRAPH" | "MASTER";

export type ConnectionFilter = "ALL" | "UNCONNECTED";

/**
 * An archived or deleted policy keeps its mappings for the record, but it no
 * longer takes part in any review.
 */
export function isInactive(item: ApplicabilityItem): boolean {
  return item.lifecycleStatus !== "ACTIVE";
}

/** Draft versions shown in the working matrix that have not been saved and activated. */
export function isUnpublishedDraft(item: ApplicabilityItem): boolean {
  return !isInactive(item) && item.editable;
}

export function sageWorkflowItemHref(itemType: "POLICY" | "RULE", itemId: string): string {
  return `/sage-workflow?item=${itemType}:${itemId}`;
}

/** Whether the item still covers any target, and so still appears in the scope. */
export function isMapped(item: ApplicabilityItem): boolean {
  return (
    item.appliesToAllChannels ||
    item.appliesToAllCategories ||
    Object.keys(item.channelNotesByChannelId).length > 0 ||
    item.categoryIds.length > 0
  );
}

export function matrixItemKey(item: ApplicabilityItem): string {
  return `${item.itemType}:${item.itemId}`;
}

/** The four visual source groups shown in the Sage Workflow mindmap. */
export type SourceGroupType = "POLICY" | "RULE_CUSTOM" | "RULE_TAUGHT" | "DICTIONARY";

/** Custom Rules and Educate Sage rules share the same RULE itemType — origin tells them apart. */
export function groupTypeOfItem(item: ApplicabilityItem): "POLICY" | "RULE_CUSTOM" | "RULE_TAUGHT" {
  if (item.itemType === "POLICY") return "POLICY";
  return item.origin === "TAUGHT" ? "RULE_TAUGHT" : "RULE_CUSTOM";
}

const DRAFT_GROUP_ORDER = ["POLICY", "RULE_CUSTOM", "RULE_TAUGHT"] as const;

export function orderedUnpublishedDrafts(drafts: ApplicabilityItem[]): ApplicabilityItem[] {
  return DRAFT_GROUP_ORDER.flatMap((groupType) =>
    drafts.filter((item) => groupTypeOfItem(item) === groupType),
  );
}

export function nextDraftAfter(
  drafts: ApplicabilityItem[],
  currentKey: string,
): ApplicabilityItem | null {
  const ordered = orderedUnpublishedDrafts(drafts);
  const index = ordered.findIndex((item) => matrixItemKey(item) === currentKey);
  if (index < 0) return ordered[0] ?? null;
  return ordered[index + 1] ?? null;
}

export function targetCellKey(channelId: string, categoryId: string): string {
  return `${channelId} ${categoryId}`;
}

export function splitTargetCellKey(key: string): ApplicabilityTargetCell {
  const [channelId, categoryId] = key.split(" ");
  return { channelId: channelId!, categoryId: categoryId! };
}

export function leafCategories(
  categories: Awaited<ReturnType<typeof api.listMaterialCategories>>,
) {
  const active = categories.filter((category) => category.isActive);
  const parentIds = new Set(active.flatMap((category) => category.parentId ? [category.parentId] : []));
  return active.filter((category) => !parentIds.has(category.id));
}

export function expandedTargetCells(
  item: ApplicabilityItem,
  channels: Awaited<ReturnType<typeof api.listChannels>>,
  categories: Awaited<ReturnType<typeof api.listMaterialCategories>>,
): Set<string> {
  if (item.targetCells.length > 0) {
    return new Set(item.targetCells.map((cell) => targetCellKey(cell.channelId, cell.categoryId)));
  }
  const activeChannels = channels.filter(
    (channel) =>
      channel.isActive &&
      (item.appliesToAllChannels || channel.id in item.channelNotesByChannelId),
  );
  const activeCategories = leafCategories(categories).filter(
    (category) =>
      item.appliesToAllCategories ||
      item.categoryIds.includes(category.id) ||
      (category.parentId !== null && item.categoryIds.includes(category.parentId)),
  );
  return new Set(
    activeChannels.flatMap((channel) =>
      activeCategories.map((category) => targetCellKey(channel.id, category.id)),
    ),
  );
}

export function connectedItemsForCell(
  items: ApplicabilityItem[],
  selections: Map<string, Set<string>>,
  cellKey: string,
): ApplicabilityItem[] {
  return items.filter(
    (item) => !isInactive(item) && Boolean(selections.get(matrixItemKey(item))?.has(cellKey)),
  );
}

export function cellDraftCount(
  items: ApplicabilityItem[],
  selections: Map<string, Set<string>>,
  cellKey: string,
): number {
  return connectedItemsForCell(items, selections, cellKey).filter(isUnpublishedDraft).length;
}

export function groupLeafCategories(
  leaves: Awaited<ReturnType<typeof api.listMaterialCategories>>,
  allCategories: Awaited<ReturnType<typeof api.listMaterialCategories>>,
) {
  const groups = new Map<string, { id: string; name: string; categories: typeof leaves }>();
  for (const category of leaves) {
    const parent = allCategories.find((candidate) => candidate.id === category.parentId);
    const id = parent?.id ?? category.id;
    const group = groups.get(id) ?? {
      id,
      name: parent?.name ?? category.name,
      categories: [],
    };
    group.categories.push(category);
    groups.set(id, group);
  }
  return [...groups.values()];
}

export function cellsFromChannelAndCategoryIds(
  channelIds: Iterable<string>,
  categoryIds: Iterable<string>,
): Set<string> {
  const cells = new Set<string>();
  for (const channelId of channelIds) {
    for (const categoryId of categoryIds) {
      cells.add(targetCellKey(channelId, categoryId));
    }
  }
  return cells;
}

export function coverageFromCells(
  cells: Set<string>,
  channels: Awaited<ReturnType<typeof api.listChannels>>,
  categories: Awaited<ReturnType<typeof api.listMaterialCategories>>,
): { channelIds: Set<string>; categoryIds: Set<string> } {
  const activeChannels = channels.filter((channel) => channel.isActive);
  const leaves = leafCategories(categories);
  return {
    channelIds: new Set(
      activeChannels
        .filter((channel) => leaves.some((category) => cells.has(targetCellKey(channel.id, category.id))))
        .map((channel) => channel.id),
    ),
    categoryIds: new Set(
      leaves
        .filter((category) =>
          activeChannels.some((channel) => cells.has(targetCellKey(channel.id, category.id))),
        )
        .map((category) => category.id),
    ),
  };
}

export function isFullCoverage(
  cells: Set<string>,
  channels: Awaited<ReturnType<typeof api.listChannels>>,
  categories: Awaited<ReturnType<typeof api.listMaterialCategories>>,
): boolean {
  const activeChannels = channels.filter((channel) => channel.isActive);
  const leaves = leafCategories(categories);
  if (activeChannels.length === 0 || leaves.length === 0) return false;
  return cells.size === activeChannels.length * leaves.length;
}

export function sameCellCoverage(left: Set<string>, right: Set<string>): boolean {
  if (left.size !== right.size) return false;
  for (const key of left) {
    if (!right.has(key)) return false;
  }
  return true;
}

export function inputFromTargetCells(cells: Set<string>): UpdateApplicabilityInput {
  const targetCells = [...cells].map(splitTargetCellKey);
  const channelIds = [...new Set(targetCells.map((cell) => cell.channelId))];
  const categoryIds = [...new Set(targetCells.map((cell) => cell.categoryId))];
  return {
    appliesToAllChannels: false,
    appliesToAllCategories: false,
    channels: channelIds.map((channelId) => ({ channelId })),
    categoryIds,
    targetCells,
  };
}

/** Prefer all-channel / all-category flags when the working matrix is a complete grid. */
export function inputFromCoverage(
  cells: Set<string>,
  channels: Awaited<ReturnType<typeof api.listChannels>>,
  categories: Awaited<ReturnType<typeof api.listMaterialCategories>>,
): UpdateApplicabilityInput {
  const activeChannels = channels.filter((channel) => channel.isActive);
  const leaves = leafCategories(categories);
  const { channelIds, categoryIds } = coverageFromCells(cells, channels, categories);
  const cartesian = cellsFromChannelAndCategoryIds(channelIds, categoryIds);
  const completeGrid = sameCellCoverage(cells, cartesian) && cartesian.size > 0;
  if (!completeGrid) return inputFromTargetCells(cells);

  const appliesToAllChannels = channelIds.size === activeChannels.length;
  const appliesToAllCategories = categoryIds.size === leaves.length;
  return {
    appliesToAllChannels,
    appliesToAllCategories,
    channels: appliesToAllChannels ? [] : [...channelIds].map((channelId) => ({ channelId })),
    categoryIds: appliesToAllCategories ? [] : [...categoryIds],
    targetCells: [],
  };
}
